import { unique, uniqueByKey } from "@helpers"
import useNameAttribute from "@hooks/attributes/useNameAttribute"
import useAttribute from "@hooks/useAttribute"
import _ from "lodash"
import { useEffect } from "react"

export default function useAttributes(
    salon: Model.Response<Customer.Resources.Salon>
): Customer.Models.Salon.Attrs {
    const logos = useLogosAttribute(salon)
    const acceptances = useAcceptancesAttribute(salon)
    const name = useNameAttribute(salon)
    const trackings = useTrackingsAttribute(salon)
    const participations = useParticipationsAttribute(salon)

    const has = {
        awards: useHasAwardsAttribute(salon)
    }

    return {
        logos,
        acceptances,
        name,
        trackings,
        participations,
        has
    }
}

function useParticipationsAttribute(salon: Model.Response<Customer.Resources.Salon>) {
    return useAttribute(salon.data, salon => {
        const result: Customer.Resources.Participation[] | Customer.Resources.PastParticipation[] = []

        salon.entry.forEach(section => {
            section.participations.forEach((participation: Customer.Resources.PastParticipation) => {
                result.push(participation)
            })
        })

        return result
    }, [])
}

function useTrackingsAttribute(salon: Model.Response<Customer.Resources.Salon>) {
    return useAttribute(salon.data, salon => {
        if (salon.type !== "finished")
            return []

        const result: Customer.Resources.Tracking[] = []

        salon.entry.forEach(section => {
            section.participations.forEach((participation: Customer.Resources.PastParticipation) => {
                if (participation.tracking)
                    result.push(participation.tracking)
            })
        })

        return result.filter(uniqueByKey("number"))
    }, [])
}

function useHasAwardsAttribute(salon: Model.Response<Customer.Resources.Salon>) {
    return useAttribute(salon.data, salon => {
        if (salon.type !== 'finished')
            return false

        return Boolean(salon.entry.find(section => section.participations.find(
            (participation: Customer.Resources.PastParticipation) => participation.award
        )))
    }, false)
}

function useLogosAttribute(salon: Model.Response<Customer.Resources.Salon>): Customer.Models.Salon.Attrs.Logos {
    return useAttribute(salon.data, salon => {
        const logos = salon.patronages.map(item => ({ url: item.organization.logo_url, patronage: item.number }))
        return _.uniq(logos)
    }, [])
}

function useAcceptancesAttribute(salon: Model.Response<Customer.Resources.Salon>):  Customer.Models.Salon.Attrs.Acceptances {
    return useAttribute(salon.data, (salon: Customer.Resources.Salon.Past) => {
        if (salon.status !== "past")
            return []

        const result: Customer.Resources.PastParticipation[] = []

        for (const section of salon.entry) {
            for (const participation of section.participations) {
                if (participation.is_accepted)
                    result.push(participation)
            }
        }

        return result
    }, [])
}